import { Button, cn } from "@tellescope/design-system";
import { BanknoteArrowDown, CreditCard } from "lucide-react";
import { BlockProps } from "../../../types";
import { usePaymentsBlock } from "../../../hooks";
import { BlockHeader } from "../BlockHeader";
import { BlockContent } from "../BlockContent";
import { PaymentsRecordsList } from "./PaymentsRecordsList";
import { PurchaseDetails } from "./PurchaseDetails";
import { LogPurchaseForm } from "./LogPurchaseForm";
import { ChargePaymentForm } from "./ChargePaymentForm";
import { useTranslation } from "react-i18next";
import { BlockEmptyState } from "../BlockEmptyState";

export const PaymentsBlock = ({
  onMaximize,
  alwaysShowButtons = false,
  className,
  contentClassName,
  ...rest
}: BlockProps) => {
  const { t } = useTranslation("payments");
  const {
    parsedInfo,
    enduser,
    activeSubsection,
    openSubsection,
    closeSubsection,
    selectedPurchase,
    openPurchase,
    closePurchase,
    isEditingTitle,
    setIsEditingTitle,
    newTitle,
    setNewTitle,
    editMode,
    setEditMode,
    updateInfo,
    duplicateBlock,
    deleteBlock,
    toggleExpanded,
    expanded,
    blockLayout,
    canEditTitle,
    canCreateBlock,
    canDeleteBlock,
  } = usePaymentsBlock({ ...rest, onMaximize, alwaysShowButtons, className });

  return (
    <div className={cn("group", className)}>
      <BlockHeader
        editMode={editMode}
        setEditMode={setEditMode}
        isEditingTitle={isEditingTitle}
        setIsEditingTitle={setIsEditingTitle}
        newTitle={newTitle}
        setNewTitle={setNewTitle}
        canEditTitle={canEditTitle}
        parsedInfo={parsedInfo}
        updateInfo={updateInfo}
        duplicateBlock={duplicateBlock}
        deleteBlock={deleteBlock}
        canCreateBlock={canCreateBlock}
        canDeleteBlock={canDeleteBlock}
        onMaximize={onMaximize}
        alwaysShowButtons={alwaysShowButtons}
        actions={
          editMode &&
          !activeSubsection &&
          !selectedPurchase && (
            <>
              <Button
                iconOnly
                variant="ghost"
                size="sm"
                color="muted"
                hoverLabel={t("Log Purchase")}
                onClick={() => openSubsection("log-purchase")}
              >
                <BanknoteArrowDown />
              </Button>
              {!!enduser?.stripeCustomerId && (
                <Button
                  iconOnly
                  variant="ghost"
                  size="sm"
                  color="muted"
                  hoverLabel={t("Charge Payment")}
                  onClick={() => openSubsection("charge")}
                >
                  <CreditCard />
                </Button>
              )}
            </>
          )
        }
      />

      <BlockContent
        editMode={editMode}
        setEditMode={setEditMode}
        expanded={expanded}
        layout={blockLayout}
        onToggleExpanded={() => toggleExpanded()}
        className={cn("flex flex-col", contentClassName)}
      >
        {!enduser ? (
          <div className="flex flex-1 items-center justify-center">
            <BlockEmptyState>{t("Loading payments...")}</BlockEmptyState>
          </div>
        ) : activeSubsection === "log-purchase" ? (
          <LogPurchaseForm contactId={enduser.id} onSubmit={closeSubsection} onCancel={closeSubsection} />
        ) : activeSubsection === "charge" ? (
          <ChargePaymentForm contactId={enduser.id} onSubmit={closeSubsection} onCancel={closeSubsection} />
        ) : selectedPurchase ? (
          <PurchaseDetails purchase={selectedPurchase} onBack={closePurchase} />
        ) : (
          <PaymentsRecordsList contact={enduser} showDetails={expanded} onSelectPurchase={openPurchase} />
        )}
      </BlockContent>
    </div>
  );
};
